
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";
import * as HLF from "html-living-framework";
import { makeSpine, makeSprite, setParent } from "html-living-framework/lib/Utils/PixiUtils";
import { add } from "html-living-framework/lib/Utils/PointUtils";
import {floatBetween} from "html-living-framework/lib/Utils/Random";

import { Application } from "Application";
import { SPRITESHEET_UI, TWEEN_ONUPDATE } from "Data/Constants";
import type { LocationID } from "Data/GameConfig";
import * as Topics from "Data/Topics";
import { PixelPerfectHitArea } from "html-living-framework/lib/Input";
import type { IHidingSpotState } from "Interfaces/IHidingSpotState";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

export class ExteriorLuna extends PIXI.Container {
    // children
    public readonly bg: PIXI.Sprite;
    public readonly spine: PIXI.spine.Spine;
    protected spineContainer: PIXI.Container;
    // state
    protected location?: LocationID;
    protected _subscriptions: any[] = [];
    protected queuedBlink?: TimelineLite;
    protected isTalking: boolean = false;

    private readonly hat: PIXI.Container;
    // config
    private readonly hatPositionOffset = new PIXI.Point(58, 10);

    private readonly idleAnimations = ["thinking", "gesture", "searching"];

    public constructor() {
        super();
        this.bg = this.addChild(makeSprite("luna-container", SPRITESHEET_UI));
        this.bg.interactive = true;
        this.bg.hitArea = new PixelPerfectHitArea(Application.instance.renderer, this.bg);
        this.spineContainer = this.addChild(new PIXI.Container());
        this.spine = this.spineContainer.addChild(makeSpine("luna"));
        this.spine.scale.set(1 / 3);
        this.spine.position.x = 10; // eyeballed
        this.spine.position.y = 112; // eyeballed
        this.spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_FOUND, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HIDING_SPOT_SEARCHED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.HOME_BUTTON_PRESSED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.VOICEOVER_STARTED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.VOICEOVER_ENDED, this.onMessageReceived.bind(this)),
        );

        this.hat = new PIXI.Container();
        this.spineContainer.addChild(this.hat);

        const hatsprite = this.hat.addChild(makeSprite("hat-front", SPRITESHEET_UI));
        hatsprite.scale.set(this.spine.scale.y * 2);
        hatsprite.anchor.set(1, 1);
        hatsprite.position.set(-8, 2); // eyeballed
        hatsprite.rotation = 90 * PIXI.DEG_TO_RAD;
        // hatsprite.tint = 0x00ff00;
        // hatsprite.alpha = 1;

        this.hat.visible = false;

        this.ShowForegroundHat = this.ShowForegroundHat.bind(this);
        this.HideForegroundHat = this.HideForegroundHat.bind(this);
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        this.location = pData.id;
        if (this.spine.state.hasAnimation("idle")) {
            this.playAnimation("idle", true);
        }
        else {
            const track = this.spine.state.setAnimation(0, "blink", false);
            track.trackTime = track.animationEnd;
            this.spine.update(0);
        }
        return this;
    }

    public animateIn() {
        let now: number = PIXI.ticker.shared.lastTime;
        let lastTick: number = 0;
        let deltaTime: number = 0;
        TweenLite
            .from(this, 0.5, {pixi: {positionY: 40 }})
            .eventCallback(TWEEN_ONUPDATE, () => {
                lastTick = now;
                now = PIXI.ticker.shared.lastTime;
                deltaTime = (now - lastTick) / 1000;
                this.update(deltaTime);
            })
        ;
    }

    public onResize(pSize: PIXI.Point) {
        //
    }

    public update(pDeltaTime: number): void {
        this.spine.update(pDeltaTime);
        if (this.hat.visible) {
            const bone = this.spine.skeleton.findBone("hat");
            const p = new PIXI.Point(
                (bone.worldX * this.spine.scale.x) + this.spine.position.x,
                (bone.worldY * this.spine.scale.y) + this.spine.position.y,
            );
            this.hat.position.copy(this.hat.parent.toLocal(this.spineContainer.toGlobal(p)));
            this.hat.rotation = (bone.getWorldRotationY()) * PIXI.DEG_TO_RAD;
        }
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        TweenLite.killTweensOf(this);
        TweenLite.killDelayedCallsTo(this.ShowForegroundHat);
        TweenLite.killDelayedCallsTo(this.HideForegroundHat);
        this.queuedBlink?.kill();
        this.spine.removeAllListeners();
        this.hat.destroy();
        super.destroy(pOptions);
    }

    public getHatPosition(): PIXI.Point {
        const bone = this.spine.skeleton.findBone("hat");
        const p = new PIXI.Point(
            (bone.worldX * this.spine.scale.x) + this.spine.position.x,
            (bone.worldY * this.spine.scale.y) + this.spine.position.y,
        );
        return this.spineContainer.toGlobal(add(p, this.hatPositionOffset));
    }

    protected StartTalking() {
        const wasTalking = this.isTalking;
        if (!wasTalking) {
            // console.log("begin lip flap");
        }
        else {
            return;
        }
        this.isTalking = true;
        this.spine.state.setAnimation(1, "talking", true);

        let track = this.spine.state.tracks[0];
        if (!track?.animation.name.endsWith("-talk")) {
            const newAnim = track.animation.name + "-talk";
            if (this.spine.state.hasAnimation(newAnim)) {
                // console.log(newAnim);
                this.spine.state.setAnimation(0, newAnim, track.loop).trackTime = track.trackTime;
            }
            else {
                track = this.spine.state.setAnimation(0, "blink", false);
                track.trackTime = track.animationEnd;
                // console.log("paused at end of blink animation");
            }
        }
    }

    protected StopTalking() {
        const wasTalking = this.isTalking;
        if (wasTalking) {
            // console.log("end lip flap");
        }
        else {
            return;
        }
        this.isTalking = false;
        this.spine.state.setEmptyAnimation(1, 0);

        const track = this.spine.state.tracks[0];
        if (!track?.animation.name.endsWith("-talk")) {
            const newAnim = track.animation.name + "-talk";
            if (this.spine.state.hasAnimation(newAnim)) {
                // console.log(newAnim);
                this.spine.state.setAnimation(0, newAnim, track.loop).trackTime = track.trackTime;
            }
        }
    }

    protected ShowForegroundHat() {
        this.hat.visible = true;
        setParent(this.hat, Application.instance.state); // draw on top of everything else
    }

    protected HideForegroundHat() {
        this.hat.visible = false;
        setParent(this.hat, this.spineContainer);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ITEM_FOUND:
                this.PlayItemFoundAnimation(pData as IItemState);
                break;
            case Topics.HIDING_SPOT_SEARCHED:
                if ((pData as IHidingSpotState).itemID === undefined) {
                    this.PlayEmptyHidingSpotSearchedAnimation(pData as IHidingSpotState);
                }
                break;
            case Topics.HOME_BUTTON_PRESSED:
                this.HideForegroundHat();
                break;
            case HLF.VOICEOVER_STARTED:
                this.StartTalking();
                break;
            case HLF.VOICEOVER_ENDED:
                this.StopTalking();
                break;
        }
    }

    protected PlayEmptyHidingSpotSearchedAnimation(state: IHidingSpotState) {
        // TODO: animation
    }

    protected PlayItemFoundAnimation(state: IItemState) {
        // PubSub.publishSync(LOCK_INPUT, true);
        TweenLite.killDelayedCallsTo(this.ShowForegroundHat);
        TweenLite.killDelayedCallsTo(this.HideForegroundHat);
        this.HideForegroundHat();
        TweenLite.delayedCall(2.666, this.ShowForegroundHat);
        this.playAnimation("idle-hat-off", false);
    }

    private PlayIdleAnimation() {
        if (this.spine.state.hasAnimation("idle")) {
            this.playAnimation("idle", true);
        }
        this.queuedBlink?.kill();
        this.queuedBlink = new TimelineLite();
        this.queuedBlink
            .call(() => {
                // 25% of the time we play an idle animation instead of blink
                if (!this.isTalking && Math.random() > 0.75) {
                    const nextAnimation = this.idleAnimations.shift();
                    this.playAnimation(nextAnimation!, false);
                    this.idleAnimations.push(nextAnimation!);
                }
                // 75% of the time we blink
                // or 100% of the time while talking we blink
                else {
                    this.PlayBlinkAnimation();
                }
            })
            // every 1-2 seconds
            .delay(floatBetween(1, 2))
        ;
    }

    private PlayBlinkAnimation() {
        this.playAnimation("blink", false);
    }

    private playAnimation(pAnimationName: string, pLoop: boolean = false) {
        this.queuedBlink?.kill();
        if (this.isTalking) {
            if (this.spine.state.hasAnimation(pAnimationName + "-talk")) {
                pAnimationName += "-talk";
            }
        }
        // console.log(pAnimationName);
        this.spine.state.setAnimation(0, pAnimationName, pLoop);
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        if (pTrack.trackIndex === 0) {
            switch (pTrack.animation.name) {
                case "idle-hat-off":
                case "idle-hat-off-talk":
                    this.playAnimation("idle-hat-on", false);
                    TweenLite.delayedCall(1, this.HideForegroundHat);
                    break;
                case "idle-hat-on":
                case "idle-hat-on-talk":
                    // PubSub.publishSync(UNLOCK_INPUT, false);
                    this.PlayIdleAnimation();
                    break;
                case "thinking":
                case "thinking-talk":
                case "blink":
                case "searching":
                case "searching-talk":
                case "gesture":
                case "gesture-talk":
                default:
                    this.PlayIdleAnimation();
                    break;
            }
        }
    }
}
